import { AriaDatapoint, isClientVerboseQueryStringEnabled } from './AriaDatapoint';
import { DatapointOptions } from 'owa-analytics-actions';
import { getQueryStringParameter, hasQueryStringParameter } from 'owa-querystring';
import { getUniquePropertyString } from '../utils/getUniquePropertyString';
import { hasUserWindowHadFocusSince } from '../utils/visibilityState';
import { lazyLogDatapoint } from '../lazyFunctions';
import { ServiceFetchError } from 'owa-service/lib/ServiceFetchError';
import { DatapointStatus, ErrorKeys, GenericKeys, PropertyBagKeys } from '../types/DatapointEnums';
import { safeRequestAnimationFrame } from 'owa-performance';
import { getThroughEdge, getDag, getServerVersion } from 'owa-config';
import { getGlobalImportStartTime } from 'owa-bundling-light';

const performanceMarktQueryStringParam = 'pm';
const performanceMarkPrefix = 'owa-';
const defaultDatapointTimeout = 60 * 1000; // 60 seconds
let windowDefined = typeof window !== 'undefined';
let performanceMarkEnabled = windowDefined && window.performance && !!window.performance.mark;

let datapointLayoutScore: number = -1;
export function setDatapointLayoutScore(score: number) {
    datapointLayoutScore = score;
}

export function getServerErrorName(errorResponse: Response) {
    return (
        errorResponse?.headers &&
        (errorResponse.headers.get('x-owa-error') ||
            errorResponse.headers.get('x-auth-error') ||
            errorResponse.headers.get('x-redir-error') ||
            errorResponse.headers.get('x-jit-error'))
    );
}

export function getServerInnerError(errorResponse: Response) {
    return errorResponse?.headers && errorResponse.headers.get('x-innerexception');
}

export async function getErrorMessageId(errorResponse: Response): Promise<string> {
    if (!errorResponse) {
        return undefined;
    }

    let errorFromHeader = errorResponse.headers
        ? errorResponse.headers.get('x-owaerrormessageid')
        : undefined;

    if (errorFromHeader) {
        return errorFromHeader;
    } else {
        try {
            const errorJson = await errorResponse.clone().json();
            return errorJson.Body ? errorJson.Body.ResponseCode : undefined;
        } catch {
            return undefined;
        }
    }
}

export class PerformanceDatapoint extends AriaDatapoint {
    protected hasEnded = false;
    private startTime: number;
    private startScore: number;
    private timeBeforePause: number = 0;
    status: DatapointStatus;
    waterfallTimings: { [key: string]: number };
    constructor(eventName: string, options?: DatapointOptions) {
        super(eventName, null, options);
        const importTime = getGlobalImportStartTime();
        this.status = DatapointStatus.Success;
        this.startTime = importTime || Date.now();
        this.startScore = datapointLayoutScore;
        this.performanceMark('s');

        if (importTime) {
            this.addData('BundleTime', Date.now() - importTime, true);
        }

        // if the the datapoint doesn't end in 30 seconds, then end it with a timeout error
        setTimeout(
            this.endWithTimeout.bind(this),
            options && typeof options.timeout == 'number'
                ? options.timeout
                : defaultDatapointTimeout
        );
    }
    isPeformanceMarkEnabled(): boolean {
        if (!performanceMarkEnabled) {
            return false;
        }

        return (
            hasQueryStringParameter(performanceMarktQueryStringParam) &&
            (!hasQueryStringParameter(performanceMarktQueryStringParam) ||
                this.eventName
                    .toLowerCase()
                    .indexOf(
                        getQueryStringParameter(performanceMarktQueryStringParam).toLowerCase()
                    ) > -1)
        );
    }
    getEventType(): string {
        if (this.options && this.options.isQos) {
            return 'client_qos';
        }

        return super.getEventType();
    }
    addCheckmark(checkmarkName: string): number {
        this.performanceMark(checkmarkName);
        if (!this.waterfallTimings) {
            this.waterfallTimings = {};
        }

        const time = this.timeFromStart();
        try {
            checkmarkName = getUniquePropertyString(this.waterfallTimings, checkmarkName);
            this.waterfallTimings[checkmarkName] = time;
        } catch {
            // We add checkmarks automatically within the action framework and there can be a lot
            // with the same exact name. So if we can't find a unique property name, don't add the
            // checkmark
        }
        return time;
    }

    async endWithError(status: DatapointStatus, error?: Error, duration?: number): Promise<void> {
        this.status = status;
        const fetchError = error as ServiceFetchError;
        if (fetchError?.response) {
            const errorResponse = fetchError.response;
            this.addErrorHeaders(errorResponse.headers);

            const errorMessageId = await getErrorMessageId(errorResponse);
            this.addCustomProperty(PropertyBagKeys.errorMsgId, errorMessageId);

            this.addDataWithPiiScrubbing(PropertyBagKeys.errorReqUrl, errorResponse.url);
            this.addCustomProperty(PropertyBagKeys.errorStatusCode, errorResponse.status);

            this.addDataWithPiiScrubbing(ErrorKeys.errorMsg, getServerErrorName(errorResponse));
            this.addDataWithPiiScrubbing(
                ErrorKeys.errorDetails,
                `Server InnerException: ${getServerInnerError(errorResponse)}; client stack: ${
                    error.stack
                }`
            );
        } else if (fetchError?.responseMessage) {
            let responseMessage = fetchError.responseMessage;
            this.addErrorHeaders((responseMessage as any).originalFetchHeaders);
            this.addCustomProperty(PropertyBagKeys.errorStatusCode, responseMessage.ResponseCode);
            this.addDataWithPiiScrubbing(
                ErrorKeys.errorMsg,
                responseMessage.ResponseClass + ':' + responseMessage.MessageText
            );
            this.addDataWithPiiScrubbing(ErrorKeys.errorDetails, responseMessage.StackTrace);
        } else if (error) {
            this.addDataWithPiiScrubbing(ErrorKeys.errorMsg, error.message);
            this.addDataWithPiiScrubbing(ErrorKeys.errorDetails, error ? error.stack : '');
        }
        this.end(duration);
    }
    endAfterAnimationFrame() {
        safeRequestAnimationFrame(() => {
            this.end();
        });
    }
    end(duration?: number, overrideStatus?: DatapointStatus): void {
        if (!this.hasEnded) {
            this.performanceMark('e');
            // if duration is not passed in, then calculate the time based of the current time it was ended and the time the datapoint was created.
            this.addData(
                GenericKeys.e2eTimeElapsed,
                typeof duration === 'number'
                    ? Math.floor(duration)
                    : this.timeFromStart() + this.timeBeforePause,
                true
            );
            if (this.startScore > -1) {
                this.addData('LayoutScore', datapointLayoutScore - this.startScore);
            }

            this.addData(GenericKeys.status, overrideStatus || this.status);
            if (
                (isClientVerboseQueryStringEnabled() ||
                    (this.options && this.options.logVerbose)) &&
                this.waterfallTimings
            ) {
                this.addData(GenericKeys.Waterfall, JSON.stringify(this.waterfallTimings));
            }
            if (this.options && this.options.isQos) {
                this.addData(GenericKeys.throughAFD, getThroughEdge());
                this.addData(GenericKeys.serviceVersion, getServerVersion());
                this.addData(GenericKeys.dag, getDag());
            }

            this.hasEnded = true;
            lazyLogDatapoint.importAndExecute(this);
        }
    }
    markUserPerceivedTime(waitForAnimationFrame?: boolean) {
        if (waitForAnimationFrame) {
            safeRequestAnimationFrame(this.addUserPerceivedTime.bind(this));
        } else {
            this.addUserPerceivedTime();
        }
    }
    invalidate() {
        this.hasEnded = true;
    }
    pause() {
        this.timeBeforePause += this.timeFromStart();
        this.startTime = null;
    }
    resume() {
        if (!this.startTime) {
            this.startTime = Date.now();
        }
    }
    private addUserPerceivedTime(): void {
        this.addData(GenericKeys.UserPerceivedTime, this.timeFromStart());
    }
    private performanceMark(value: string) {
        if (this.isPeformanceMarkEnabled()) {
            window.performance.mark(performanceMarkPrefix + this.eventName + '_' + value);
        }
    }
    private endWithTimeout() {
        if (!this.hasEnded && hasUserWindowHadFocusSince(this.startTime)) {
            this.endWithError(DatapointStatus.Timeout);
        }
    }
    private timeFromStart(): number {
        return this.startTime != null ? Date.now() - this.startTime : 0;
    }
    private addErrorHeaders(headers: Headers) {
        if (headers && typeof headers.get == 'function') {
            this.addServerHeaders(headers);
            this.addCustomProperty(PropertyBagKeys.errorRequestsId, headers.get('request-id'));
            this.addCustomProperty(PropertyBagKeys.errorAFDRef, headers.get('x-msedge-ref'));
        }
    }
}
